'use client'

import React, { useEffect, useState } from 'react'
import { http } from '../../../utils/clientHttp'
import { CircularProgress, Grow } from '@mui/material'
import Text from '../../Text'
import styles from './styles.module.css'
import SearchInput from '../../SearchInput'
import SmartLink from '../../SmartLink'
import Spacer from '../../Spacer'
import SimpleBlockContent from '../../SimpleBlockContent'
import { toSrc } from '../../../utils/imageSrc'

const cn = (...a) => a.filter(Boolean).join(' ')

function DataPacksList({ backgroundColor, textColor, rowHoverColor, title, searchPlaceholder = 'Search by car, series, coach, etc.', failedSearchText = 'We couldn\'t find any data packs containing your search terms.' }) {

  const [isLoading, setIsLoading] = useState(true)

  const [dataPacks, setDataPacks] = useState([])
  const [dataPacksCount, setDataPacksCount] = useState(0)
  const [dataPacksLink, setDataPacksLink] = useState(null)

  const [search, setSearch] = useState(null)
  const [searchTimeoutId, setSearchTimeoutId] = useState(0)

  useEffect(() => {
    setIsLoading(true)

    http.get(`/api/searchDataPacks?searchTerm=${search || ''}`).then((response) => {
      setDataPacks(response.data.dataPacks)
      setDataPacksCount(response.data.allDataPacksCount)
      setDataPacksLink(response.data.allDataPacksLink)
      setIsLoading(false)
    }).catch((e) => {
      console.log('error', e.message)
      setDataPacks([])
      setIsLoading(false)
    })
  }, [search])

  const onSearchInputChanged = (value) => {
    clearTimeout(searchTimeoutId)

    const timerId = setTimeout(() => setSearch(value), 500)
    setSearchTimeoutId(timerId)
  }

  const colorProps = { backgroundColor: backgroundColor.value, color: textColor.value }

  return (
    <div className={styles.wrapper} style={{ background: colorProps.backgroundColor }}>
      <div className={styles.contentWrapper}>
        <SimpleBlockContent blocks={title} noSpace />
        <Spacer size={16}/>
        <SearchInput
          {...colorProps}
          disabled={isLoading}
          onInputChange={(value) => onSearchInputChanged(value)}
          placeholder={searchPlaceholder}
        />
        { !isLoading && search && dataPacks.length === 0 && <Text type="body-short-1">{failedSearchText}</Text> }
        <Grow in={isLoading} timeout={{ enter: 0, exit: 500 }}>
          <div className={isLoading ? styles.loadingShown : styles.loading} style={colorProps}>
            <CircularProgress color="inherit"/>
          </div>
        </Grow>
        <Grow in={!isLoading} timeout={{ enter: 500, exit: 0 }}>
          <div className={styles.tableWrapper} style={{ '--row-hover-color': rowHoverColor.value }}>
            <a className={cn(styles.row, dataPacks.length === 0 && styles.rowHide, styles.rowTitle, styles.rowStatic)}>
              <div className={styles.tableCellTitle}><Text type="h4">Car</Text></div>
              <div className={styles.tableCellTitle}><Text type="h4">Series</Text></div>
              <div className={styles.tableCellTitle}><Text type="h4">Coach</Text></div>
              <div className={cn(styles.tableCellTitle, styles.tableCellHideOnMobile)}><Text type="h4">Part of</Text></div>
            </a>
            { renderDataPacks(dataPacks) }
            <SmartLink href={dataPacksLink || ''} className={cn(styles.row, search && styles.rowHide, styles.rowSingleCol)}>
              <div className={styles.tableCell}>
                <Text type="body-short-1">{`See ${dataPacksCount - dataPacks.length} more`}</Text>
              </div>
            </SmartLink>
          </div>
        </Grow>
      </div>
    </div>
  )
}

const renderDataPacks = (dataPacks) => {
  if (dataPacks && dataPacks.length) {
    return dataPacks.map((d, i) => (
      <SmartLink key={d.name + i} href={d.link} className={styles.row}>
        <div className={styles.tableCell}>
          <div className={styles.carImageWrapper}>
            <div className={styles.image} style={{ backgroundImage: `url(${toSrc(d.imageUrl)})` }}/>
          </div>
          <div className={styles.carNameWrapper}>
            <Text type="body-short-1">{d.carName}</Text>
          </div>
        </div>
        <div className={styles.tableCell}>
          <Text type="body-short-1">{d.seriesName}</Text>
        </div>
        <div className={styles.tableCell}>
          <div className={styles.coachWrapper}>
            <div className={styles.coachImage} style={{ backgroundImage: `url(${toSrc(d.coachImageUrl)})` }}/>
            <Text type="body-short-1">{d.coachName}</Text>
          </div>
        </div>
        <div className={cn(styles.tableCell, styles.tableCellHideOnMobile)}>
          <div className={styles.chip}>
            <Text type="body-short-1">{d.catalogue.name}</Text>
          </div>
        </div>
      </SmartLink>
    ))
  }
}

export default DataPacksList
